import { Alert, Button, Input, Radio, Space } from "antd";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import request from "@/api";
import { areaLib } from "@/utils/areaLib";
import { useUserStore } from "@/views/stores";
import type { SampleApply, SampleApplyHandleForm } from "@zhihaoscm/entity";
import { SAMPLE_APPLY_STATE } from "@zhihaoscm/meta";

export interface SampleApplyDetailPageProps {
    loadDetail?: (id: number | string) => Promise<SampleApply>;
    handleApplication?: (id: number | string, payload: SampleApplyHandleForm) => Promise<unknown>;
    permissions?: string[];
}

/** 沿用 Vue SampleApplyService.getVo 的申请寄样详情接口。 */
export async function loadSampleApplicationDetail(id: number | string): Promise<SampleApply> {
    const response = await request.service.get<SampleApply>(`/admin/sample-apply/vo/${id}`);
    return response.data;
}

/** 沿用 Vue 申请寄样处理接口及 SampleApplyHandleForm payload。 */
export async function handleSampleApplication(id: number | string, payload: SampleApplyHandleForm): Promise<unknown> {
    const response = await request.service.put(`/admin/sample-apply/handle/${id}`, payload);
    return response.data;
}

/** 沿用 Vue 详情页的申请用户展示规则。 */
function getCustomerLabel(detail: SampleApply): string {
    const customer = detail.customerEnterprise;
    return customer?.name || `${customer?.realName || ""} | ${customer?.mobile || ""}`;
}

/** 将 Vue 省市区和详细地址拼成详情页地址文案。 */
function getAddressLabel(detail: SampleApply): string {
    const address = detail.addressInfo;
    const areaName = areaLib.getFullAreaName(address?.province, address?.city, address?.district);
    const fallbackArea = [address?.province, address?.city, address?.district].filter(Boolean).join(" ");
    return [areaName || fallbackArea, address?.detailAddress].filter(Boolean).join(" ");
}

/** 复用 Vue SAMPLE_APPLY_STATE 的状态文案，避免详情头部和操作信息口径分叉。 */
function getStateLabel(state?: number | string): string {
    return SAMPLE_APPLY_STATE.getIntroByCode(Number(state)) || "-";
}

/** 保留 Vue dateTimeFormat 的分钟级详情展示。 */
function formatDateTime(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : "-";
}

const PRODUCT_DETAIL_PERMISSIONS = ["ROLE_PRODUCT_R", "ROLE_PRODUCT_MANAGE", "ROLE_PRODUCT_DEAL", "ROLE_PRODUCT_CLOSE"];
const CUSTOMER_DETAIL_PERMISSION = "ROLE_SS_CUSTOMER_R";

/** 用户申请寄样详情页，保留 Vue 的商品、申请、操作信息分组和处理弹窗。 */
export function SampleApplyDetailPage({
    loadDetail = loadSampleApplicationDetail,
    handleApplication = handleSampleApplication,
    permissions
}: SampleApplyDetailPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    // 跨模块详情链接沿用目标路由权限，避免寄样详情用户点击后被路由守卫重定向到 403。
    const canViewProductDetail = currentPermissions.some(permission => PRODUCT_DETAIL_PERMISSIONS.includes(permission));
    const canViewCustomerDetail = currentPermissions.includes(CUSTOMER_DETAIL_PERMISSION);
    const location = useLocation();
    const navigate = useNavigate();
    const id = new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<SampleApply | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [handleVisible, setHandleVisible] = useState(false);
    const [handleState, setHandleState] = useState<number | undefined>();
    const [logisticsNumber, setLogisticsNumber] = useState("");
    const [handleRemark, setHandleRemark] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const hasPermission = currentPermissions.includes("ROLE_APPLY_SAMPLE_DEAL");

    /** 打开 Vue 处理弹窗时清空上一次填写内容。 */
    const openHandleDialog = () => {
        setHandleState(undefined);
        setLogisticsNumber("");
        setHandleRemark("");
        setErrorMessage(null);
        setHandleVisible(true);
    };

    /** 按 Vue 规则校验处理结果和已寄出状态下的物流单号。 */
    const submitHandle = async () => {
        if (!id || handleState == null) {
            setErrorMessage("请选择处理结果");
            return;
        }
        if (handleState === 2 && !logisticsNumber.trim()) {
            setErrorMessage("请填写寄出物流单号");
            return;
        }
        setSubmitting(true);
        try {
            await handleApplication(id, {
                state: handleState,
                logisticsNumber: handleState === 3 ? "" : logisticsNumber.trim(),
                handleRemark: handleRemark.trim()
            });
            setHandleVisible(false);
            setErrorMessage(null);
            const refreshed = await loadDetail(id);
            setDetail(refreshed);
        } catch {
            setErrorMessage("申请寄样处理失败");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!id) {
            setErrorMessage("缺少申请寄样编号");
            return;
        }
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (!disposed) {
                    setDetail(result);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("申请寄样详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    return (
        <section
            aria-label="sample-apply-detail-page"
            className="detail-page-transparent flex h-full min-h-full flex-col"
        >
            <div className="section-card mb-8">
                <ZhDetailHeader
                    leftInfos={[{ label: "序号", value: detail?.id || id || "-" }]}
                    rightInfos={[{ label: "申请时间", value: formatDateTime(detail?.createdTime) }]}
                    stateDesc={getStateLabel(detail?.state)}
                    title="用户申请寄样详情"
                />
            </div>
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {detail ? (
                /* 对齐旧版 detail.vue：详情分组和非待处理操作信息由 zhihao MultiGrid 统一承载。 */
                <ZhMultiGrid>
                    <div className="section-card mb-8">
                        <div className="section-title">商品信息</div>
                        <ZhGrid cols={4} gap={[16, 16]}>
                            <ZhBaseItem label="商品编号">
                                {canViewProductDetail ? (
                                    <Link to={`/productManagement/consignment/detail?id=${detail.productId}`}>
                                        {detail.productId || "-"}
                                    </Link>
                                ) : (
                                    detail.productId || "-"
                                )}
                            </ZhBaseItem>
                            <ZhBaseItem label="商品标题" value={detail.productInfo?.title || "-"} />
                            <ZhBaseItem label="品类" value={detail.productInfo?.productTypeName || "-"} />
                            <ZhBaseItem label="规格" value={detail.productInfo?.productTypeSpec || "-"} />
                        </ZhGrid>
                    </div>
                    <div className="section-card mb-8">
                        <div className="section-title">申请信息</div>
                        <ZhGrid cols={4} gap={[16, 16]}>
                            <ZhBaseItem label="申请用户">
                                {canViewCustomerDetail ? (
                                    <Link to={`/lianYunUser/userInfo?userId=${detail.customerId}`}>
                                        {getCustomerLabel(detail)}
                                    </Link>
                                ) : (
                                    getCustomerLabel(detail)
                                )}
                            </ZhBaseItem>
                            <ZhBaseItem label="收货人" value={detail.addressInfo?.contacts || "-"} />
                            <ZhBaseItem label="联系电话" value={detail.addressInfo?.cellphone || "-"} />
                            <ZhBaseItem label="收货地址" span={4} value={getAddressLabel(detail) || "-"} />
                            <ZhBaseItem label="备注" span={4} value={detail.remark || "-"} />
                        </ZhGrid>
                    </div>
                    {Number(detail.state) !== SAMPLE_APPLY_STATE.PENDING.code ? (
                        <div className="section-card mb-8">
                            <div className="section-title">操作信息</div>
                            <ZhGrid cols={4} gap={[16, 16]}>
                                <ZhBaseItem label="处理人" value={detail.handleName || "-"} />
                                <ZhBaseItem label="处理结果" value={getStateLabel(detail.state)} />
                                {Number(detail.state) !== SAMPLE_APPLY_STATE.INVALID_INFORMATION.code ? (
                                    <ZhBaseItem label="物流单号" value={detail.logisticsNumber || "-"} />
                                ) : null}
                                <ZhBaseItem label="备注" value={detail.handleRemark || "-"} />
                            </ZhGrid>
                        </div>
                    ) : null}
                </ZhMultiGrid>
            ) : null}
            <Space className="operation-fixed-btn">
                {detail && Number(detail.state) === SAMPLE_APPLY_STATE.PENDING.code && hasPermission ? (
                    <Button aria-label="处理" onClick={openHandleDialog} type="primary">
                        处理
                    </Button>
                ) : null}
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
            <ZhDialog
                open={handleVisible}
                title="处理"
                width={400}
                onCancel={() => setHandleVisible(false)}
                footer={
                    <Space>
                        <Button onClick={() => setHandleVisible(false)}>取消</Button>
                        <Button
                            aria-label="提交处理"
                            loading={submitting}
                            onClick={() => void submitHandle()}
                            type="primary"
                        >
                            提交
                        </Button>
                    </Space>
                }
            >
                <label className="block">
                    处理结果
                    <Radio.Group
                        aria-label="处理结果"
                        className="mt-2 block"
                        value={handleState}
                        onChange={event => setHandleState(event.target.value)}
                    >
                        <Radio value={2}>已寄出</Radio>
                        <Radio value={3}>无效信息</Radio>
                    </Radio.Group>
                </label>
                {handleState === 2 ? (
                    <label className="mt-4 block">
                        物流单号
                        <Input
                            aria-label="物流单号"
                            className="mt-2"
                            maxLength={100}
                            value={logisticsNumber}
                            onChange={event => setLogisticsNumber(event.target.value)}
                        />
                    </label>
                ) : null}
                <label className="mt-4 block">
                    处理备注
                    <Input.TextArea
                        aria-label="处理备注"
                        className="mt-2"
                        maxLength={100}
                        rows={3}
                        value={handleRemark}
                        onChange={event => setHandleRemark(event.target.value)}
                    />
                </label>
            </ZhDialog>
        </section>
    );
}

export default SampleApplyDetailPage;
